import React, { useState, useRef, useCallback } from "react";
import styles from "./TimeRangeDropdown.module.scss";
import Icon from "../Icon/Icon";
import { useAppSelector } from "../../../redux/hooks";
import useClickOutside from "../../CustomHooks/useClickOutside";

export type TimeRange = { label: string; code: string };

export type TimeRangeDropdownProps = {
  ranges: TimeRange[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  className?: string;
};

const TimeRangeDropdown: React.FC<TimeRangeDropdownProps> = ({
  ranges,
  selectedCode,
  onSelect,
  isLoading,
  isError,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const selected = ranges?.find((r) => r.code === selectedCode) || null;

  const handleSelect = useCallback(
    (code: string) => {
      onSelect(code);
      setIsOpen(false);
    },
    [onSelect]
  );

  if (isError || !ranges || ranges.length === 0) return null;
  if (isLoading) {
    return (
      <div className={`details_loader animate ${styles.calender_loader}`} />
    );
  }

  return (
    <div
      ref={dropdownRef}
      className={`time_selector_dropdown ${styles.time_selector_dropdown} ${className ?? ""}`}
    >
      <div
        className={styles.input_wrapper}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={styles.selected_date}>
          {selected ? selected.label : "Select range"}
        </span>
        <Icon
          name="down_filled_arrow"
          size={12}
          color={currentTheme === "light" ? "#2c2c2c" : "#D8D8D9"}
        />
      </div>

      {isOpen && (
        <div className={`dropdown_list ${styles.dropdown_list}`}>
          {ranges.map(({ label, code }) => (
            <div
              key={code}
              className={`dropdown_item ${styles.dropdown_item} ${
                selectedCode === code ? "selected" : ""
              }`}
              onClick={() => handleSelect(code)}
            >
              <span>{label}</span>
              {selectedCode === code && (
                <Icon
                  name="tick_icon"
                  size={17}
                  color={currentTheme === "light" ? "#5969D6" : "#99B2F5"}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(TimeRangeDropdown);
